import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from '../firebase';
import './ApplyToTutor.css';

function ApplyToTutor() {
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCourses, setExpandedCourses] = useState({}); // Track expanded descriptions

  // File states
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [idFile, setIdFile] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/courses`);
        setCourses(res.data);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses.');
      }
    };
    fetchCourses();
  }, []);

  const toggleCourse = (courseId) => {
    setSelectedCourses(prev => {
      const newSelection = { ...prev };
      if (newSelection[courseId]) {
        delete newSelection[courseId];
      } else {
        newSelection[courseId] = { onlinePrice: '', faceToFacePrice: '' };
      }
      return newSelection;
    });
  };

  const toggleDescription = (courseId) => {
    setExpandedCourses(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const updatePrice = (courseId, field, value) => {
    setSelectedCourses(prev => ({
      ...prev,
      [courseId]: {
        ...prev[courseId],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSubmitted(false);

    if (!transcriptFile || !idFile) {
      setError('Please upload both transcript and ID files.');
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();

      const formattedCourses = Object.entries(selectedCourses).map(
        ([courseId, prices]) => ({
          courseId,
          onlinePrice:
            prices.onlinePrice !== '' ? parseFloat(prices.onlinePrice) : null,
          faceToFacePrice:
            prices.faceToFacePrice !== '' ? parseFloat(prices.faceToFacePrice) : null
        })
      );

      const formData = new FormData();
      formData.append('transcript', transcriptFile);
      formData.append('idCopy', idFile);
      formData.append('courses', JSON.stringify(formattedCourses));

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/apply-to-tutor`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitted(true);
      setSelectedCourses({});
      setTranscriptFile(null);
      setIdFile(null);
    } catch (err) {
      console.error('Application error:', err);
      setError('Failed to submit application.');
    } finally {
      setLoading(false);
    }
  };

  // Filter courses based on search term
  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="apply-container">
      <div className="apply-header">
        <h2 className="apply-title">Apply to be a Tutor</h2>
        <p className="apply-subtitle">Select the courses you want to tutor and set your pricing</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {submitted && <div className="success-message">Application submitted successfully!</div>}

      {courses.length === 0 ? (
        <div className="no-courses-message">
          <p>No courses available at the moment.</p>
        </div>
      ) : (
        <div className="apply-form-container">
          <div className="search-container">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search" 
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <div className="search-results">
              {searchTerm && (
                <span className="results-count">
                  {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found
                </span>
              )}
            </div>
          </div>

          <div className="courses-selection">
            <div className="selection-header">
              <h3 className="selection-title">Available Courses</h3>
              <span className="selected-count">
                {Object.keys(selectedCourses).length} course{Object.keys(selectedCourses).length !== 1 ? 's' : ''} selected
              </span>
            </div>

            <div className="courses-list">
              {filteredCourses.map(course => {
                const isSelected = !!selectedCourses[course.id];
                const isExpanded = !!expandedCourses[course.id];

                return (
                  <div
                    key={course.id}
                    className={`course-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                  >
                    <div 
                      className="course-header"
                      onClick={() => toggleDescription(course.id)}
                    >
                      <div className="course-checkbox">
                        <input
                          type="checkbox"
                          id={`course-${course.id}`}
                          checked={isSelected}
                          onChange={e => {
                            e.stopPropagation();
                            toggleCourse(course.id);
                          }}
                          className="checkbox-input"
                        />
                        <label htmlFor={`course-${course.id}`} className="checkbox-label"></label>
                      </div>
                      <div className="course-info">
                        <h4 className="course-name">{course.name || course.id}</h4>
                        <div className="course-id">ID: {course.id}</div>
                      </div>
                      <div className="course-actions">
                        <button 
                          className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDescription(course.id);
                          }}
                          aria-label={isExpanded ? "Collapse description" : "Expand description"}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {isExpanded ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
                          </svg>
                        </button>
                        <div className={`selection-indicator ${isSelected ? 'selected' : ''}`}>
                          {isSelected ? '✓ Selected' : ''}
                        </div>
                      </div>
                    </div>

                    {isExpanded && course.description && (
                      <div className="course-description">
                        <div className="description-content">
                          <p>{course.description}</p>
                        </div>
                      </div>
                    )}

                    {isSelected && (
                      <div className="course-pricing">
                        <div className="pricing-header">
                          <h4 className="pricing-title">Set Your Pricing</h4>
                          <p className="pricing-subtitle">Enter your hourly rates (in Rands)</p>
                        </div>
                        <div className="price-inputs">
                          <div className="price-input-group">
                            <label className="price-label">
                              <span className="price-type">Online Tutoring</span>
                              <span className="price-hint">Per hour</span>
                            </label>
                            <div className="input-wrapper">
                              <span className="currency">R</span>
                              <input
                                type="number"
                                value={selectedCourses[course.id].onlinePrice}
                                onChange={e => updatePrice(course.id, 'onlinePrice', e.target.value)}
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className="price-input"
                              />
                            </div>
                          </div>
                          <div className="price-input-group">
                            <label className="price-label">
                              <span className="price-type">Face-to-Face</span>
                              <span className="price-hint">Per hour</span>
                            </label>
                            <div className="input-wrapper">
                              <span className="currency">R</span>
                              <input
                                type="number"
                                value={selectedCourses[course.id].faceToFacePrice}
                                onChange={e => updatePrice(course.id, 'faceToFacePrice', e.target.value)}
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className="price-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="file-uploads-section">
            <div className="uploads-header">
              <h3 className="uploads-title">Required Documents</h3>
              <p className="uploads-subtitle">Upload PDF copies of the following documents</p>
            </div>
            <div className="file-inputs-container">
              <div className="file-upload-card">
                <div className="file-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <div className="file-info">
                  <h4 className="file-name">{transcriptFile ? transcriptFile.name : 'Upload Transcript'}</h4>
                  <p className="file-requirement">PDF format only</p>
                </div>
                <label className="file-upload-label">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={e => setTranscriptFile(e.target.files[0])}
                    className="file-input"
                  />
                  <span className="upload-button">Choose File</span>
                </label>
              </div>

              <div className="file-upload-card">
                <div className="file-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
                <div className="file-info">
                  <h4 className="file-name">{idFile ? idFile.name : 'Upload ID Copy'}</h4>
                  <p className="file-requirement">PDF format only</p>
                </div>
                <label className="file-upload-label">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={e => setIdFile(e.target.files[0])}
                    className="file-input"
                  />
                  <span className="upload-button">Choose File</span>
                </label>
              </div>
            </div>
            
            <div className="uploads-status">
              {transcriptFile && idFile ? (
                <div className="status-complete">
                  ✓ Both documents uploaded
                </div>
              ) : (
                <div className="status-pending">
                  Please upload both documents to submit your application
                </div>
              )}
            </div>
          </div>

          <div className="submit-section">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || Object.keys(selectedCourses).length === 0 || !transcriptFile || !idFile}
              className="submit-button"
            >
              {loading ? (
                <span className="submit-loading">
                  <span className="spinner"></span>
                  Submitting Application...
                </span>
              ) : 'Submit Application'}
            </button>
            <p className="submit-note">
              Your application will be reviewed within 3-5 business days.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplyToTutor;
